import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';

import {
  useGetDimension,
  useCreateDimension,
  useUpdateDimension,
} from '@src/services/query/applang/queryHooks';
import DimensionForm, { type DimensionFormData } from '@src/features/DimensionForm';

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
`;

// Create context for interval
interface DimensionIntervalContextValue {
  interval?: [string, string];
}

const DimensionIntervalContext = createContext<DimensionIntervalContextValue>({});

// Hook to use the interval context
// Returns undefined if used outside of DimensionFormContainer
export const useDimensionInterval = () => {
  const context = useContext(DimensionIntervalContext);
  return context?.interval;
};

interface DimensionFormContainerProps {
  id?: string;
  initialValue?: DimensionFormData;
  onChange?: (dimension: DimensionFormData) => void;
  onSave?: (dimension?: Dimension | { cd_id: string }) => void;
  onCancel?: () => void;
  interval?: [string, string];
}

const DimensionFormContainer: React.FC<DimensionFormContainerProps> = ({
  id,
  initialValue,
  onChange,
  onSave,
  onCancel,
  interval,
}) => {
  const [localInitialValue, setLocalInitialValue] = useState<DimensionFormData | undefined>(
    undefined,
  );

  const shouldFetch = useMemo(() => {
    return !!id && !initialValue;
  }, [id, initialValue]);

  const {
    data: fetchedDimension,
    isLoading,
    error,
  } = useGetDimension(shouldFetch ? id : undefined);
  const { mutateAsync: createDimension, isLoading: isCreating } = useCreateDimension();
  const { mutateAsync: updateDimension, isLoading: isUpdating } = useUpdateDimension();

  const convertDimensionToFormData = useCallback((dimension: Dimension): DimensionFormData => {
    const firstRule = dimension.rules?.[0]?.[0];
    const tagKey = firstRule?.tag_key || '';
    const eventName = firstRule?.eventName;

    return {
      name: dimension.name,
      tag_key: tagKey,
      from_events: eventName && eventName.length > 0 ? 'specific' : 'all',
      specific_events: eventName || [],
    };
  }, []);

  // Convert DimensionFormData to API request format
  const convertFormDataToRules = useCallback((formData: DimensionFormData): DimensionRule[][] => {
    const rule: DimensionRule = {
      tag_key: formData.tag_key,
    };

    // Only add eventName if from_events is 'specific' and specific_events exist
    if (
      formData.from_events === 'specific' &&
      formData.specific_events &&
      formData.specific_events.length > 0
    ) {
      rule.eventName = formData.specific_events;
    }

    return [[rule]];
  }, []);

  // Update local initial value when data is fetched or initialValue changes
  useEffect(() => {
    if (initialValue) {
      setLocalInitialValue(initialValue);
    } else if (fetchedDimension) {
      const formData = convertDimensionToFormData(fetchedDimension);
      setLocalInitialValue(formData);
    } else {
      setLocalInitialValue(undefined);
    }
  }, [initialValue, fetchedDimension, convertDimensionToFormData]);

  // Handle save action
  const handleSave = useCallback(
    (formData: DimensionFormData) => {
      if (!formData) return;

      const rules = convertFormDataToRules(formData);

      // Determine if we're creating or updating based on id
      const isEditing = !!id;

      // Execute async operation without returning the promise
      void (async () => {
        try {
          if (isEditing) {
            // Wait for update and refetch to complete
            await updateDimension({
              cdId: id,
              data: {
                name: formData.name.trim(),
                rules,
              },
            });

            // For update, we pass back the updated dimension data
            // Note: API only returns message, so we construct the dimension object
            onSave?.({ ...fetchedDimension, name: formData.name, rules } as Dimension);
          } else {
            // Create mode - wait for creation and refetch to complete
            const response = await createDimension({
              name: formData.name.trim(),
              rules,
            });

            // For create, API returns { cd_id: string }
            onSave?.(response);
          }
        } catch (error) {
          // Error handling is done in the mutation hooks
          console.error('Save operation failed:', error);
        }
      })();
    },
    [id, createDimension, updateDimension, onSave, convertFormDataToRules, fetchedDimension],
  );

  // Handle cancel action
  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  // Show loading state when fetching or saving
  if (shouldFetch && isLoading) {
    return (
      <LoadingWrapper>
        <Spin size="large" tip="Loading dimension details..." />
      </LoadingWrapper>
    );
  }

  // Show error state (error messages are already shown via the hook)
  if (shouldFetch && error) {
    return (
      <LoadingWrapper>
        <div style={{ color: '#ff4d4f', textAlign: 'center' }}>
          Failed to load dimension. Please try again.
        </div>
      </LoadingWrapper>
    );
  }

  // Render the form with the resolved initial value
  return (
    <DimensionIntervalContext.Provider value={{ interval }}>
      <DimensionForm
        dimension={localInitialValue}
        isLoading={isCreating || isUpdating}
        onChange={onChange}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </DimensionIntervalContext.Provider>
  );
};

export default DimensionFormContainer;
